import {
	Action,
	ActionTypes,
	IAction,
	IFilter,
	ILastIssued,
	IMuteFilter,
	INotification,
	INotificationService,
	ISnoozeTimer,
	ISubscription,
	LastIssued,
	SnoozeTimer,
} from "./types";
import StorageHelper, { STORAGE_KEY_NOTIFICATION_PREFIX } from "./helpers/StorageHelper";
import uuidv4 from "uuid-random";
import { BaseService } from "../baseService";
import Logger from "../../clients/logger";
import ConfigClient from "../../clients/configClient";
import LauncherClient from "../../clients/launcherClient";
import StorageClient from "../../clients/storageClient";
import launcherClient from "../../clients/launcherClient";
import { StandardCallback } from "../../types";
import { Startup } from "../../clients/Startup/Startup";
import { NotificationServiceState } from "./types";
import ServiceHelper from "./helpers/ServiceHelper";
import { System } from "../../common/system";
import { fastClone } from "../../common/util";

Logger.start();
Logger.system.log("notifications Service starting up");

const NO_SOURCE = "NO_SOURCE_DEFINED";
const SNOOZE_BOOT_WAIT = 10000;

const DEPRECATED_FINSEMBLE_PREFERENCES_PATH = "finsemble.servicesConfig.notifications.userPreferences";
const FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH = "finsemble.servicesConfig.notifications";
export const NOTIFICATIONS_PUBSUB_TOPIC = "notification-ui";

/**
 * This is also specified in notificationClient. Should be exported from one to the other.
 * Internal (private) router channels, should not need to be referenced by outside of the Client src
 */
const ROUTER_ENDPOINTS = {
	ACTION_PREFIX: "action.",
	CHANNEL_PREFIX: "notification.",
	DELETE: "delete",
	FETCH_HISTORY: "fetch_history",
	LAST_ISSUED: "last_issued",
	MARK_READ: "mark_read",
	MARK_UNREAD: "mark_unread",
	MUTE: "mute",
	NOTIFY: "notify",
	PERFORM_ACTION: "perform_action",
	SUBSCRIBE: "subscribe",
	UNMUTE: "unmute",
	UNSUBSCRIBE: "unsubscribe",
	SOURCES: "sources",
	MUTE_FILTERS: "mute_filters",
	PREFERENCES: "preferences",
};

const NOTIFICATIONS_PREFERENCES_KEYS = {
	DISABLE_DOT_ON_TOOLBAR_ICON: "disableDotOnToolbarIcon",
	SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS: "showDotOnToolbarIconForMutedNotifications",
	INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS: "informationalNotificationToastPeriodSeconds",
	DEFAULT_SNOOZE_PERIOD_SECONDS: "defaultSnoozePeriodSeconds",
	NEW_NOTIFICATION_HALO_PERIOD_SECONDS: "newNotificationHaloPeriodSeconds",
	TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS: "toastTimeoutOnActionableNotifications",
};

const NOTIFICATIONS_PREFERENCES_DEFAULTS = {
	[NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON]: false,
	[NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS]: false,
	[NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS]: 45,
	[NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS]: 300,
	[NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS]: 180,
	[NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS]: true,
};

/**
 * Method to ensure the storage loading always returns a Map
 * (e.g. localStorage returns object vs indexDB returns Map)
 */
const asMap = (
	obj: object | Map<string, ISnoozeTimer | INotification | ILastIssued>
): Map<string, ISnoozeTimer | INotification | ILastIssued> => {
	if (obj instanceof Map) {
		return obj;
	}
	return new Map(Object.entries(obj));
};

/**
 * A service used to transport notification data across the system
 */
class NotificationService extends BaseService implements INotificationService {
	/**
	 * Abstracting all internal state into a single point as a way to keep track of what
	 * needs to change when implementing a solution for storage
	 */
	private storageAbstraction: {
		snoozeTimers: Map<string, ISnoozeTimer>;

		notifications: Map<string, INotification>;
		lastIssued: Map<string, ILastIssued>;
	};

	private muteFilters: IMuteFilter[];

	subscriptions: Map<string, ISubscription>;

	private proxyToWebApiFilter: IFilter | false;

	private config: any = {
		service: {},
		types: {},
	};

	private serviceStartTime: Date;

	private devMode: boolean = false;

	/**
	 * Initializes a new instance of the NotificationService class.
	 */
	constructor() {
		super({
			name: "notificationsService",
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});

		StorageHelper.logger = this.Logger;
		this.serviceStartTime = new Date();

		this.proxyToWebApiFilter = false;
		this.subscriptions = new Map<string, ISubscription>();

		this.storageAbstraction = {
			snoozeTimers: new Map<string, ISnoozeTimer>(),
			notifications: new Map<string, INotification>(),
			lastIssued: new Map<string, ILastIssued>(),
		};

		this.muteFilters = [];

		this.bindCorrectContext();
		this.onBaseServiceReady(this.initialize.bind(this));
		this.setPreferenceChangeListener();
		this.start();
	}

	/**
	 *
	 * @param {Function} callback
	 */

	initialize = async (callback: Function) => {
		this.createRouterEndpoints();
		ConfigClient.addListener({ field: FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH }, this.applyConfigChange);
		ConfigClient.getValue({ field: FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH }, this.applyConfigChange);

		ConfigClient.addListener(
			{ field: `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.mute` },
			this.applyMuteFilterChange
		);
		ConfigClient.getValue({ field: `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.mute` }, this.applyMuteFilterChange);
		await this.fetchStateFromStorage();
		this.wakeSnoozeFromLoad();
		System.getFEAInfo((info) => {
			this.devMode = info.isDevMode;
		});
		this.Logger.system.log("notifications Service ready");
		//this.serviceReady = true;
		callback();
	};

	/**
	 * Setting up the router channels/endpoints
	 */
	createRouterEndpoints() {
		this.setupNotify();
		this.setupLastIssued();
		this.setupSubscribe();
		this.setupAction();
		this.setupFetchHistory();
		this.setupMute();
		this.setupUnsubscribe();
		this.setupUIPubSub();
		this.setupMarkUnread();
		this.setupDelete();
		this.setupMarkRead();
		this.setupSources();
		this.setupMuteFilters();
		this.setupPreferences();
	}

	bindCorrectContext() {
		this.createRouterEndpoints = this.createRouterEndpoints.bind(this);
		this.subscribe = this.subscribe.bind(this);
		this.notify = this.notify.bind(this);
		this.broadcastNotification = this.broadcastNotification.bind(this);
		this.getLastIssued = this.getLastIssued.bind(this);
		this.performAction = this.performAction.bind(this);
		this.fetchHistory = this.fetchHistory.bind(this);
		this.unsubscribe = this.unsubscribe.bind(this);
		this.mute = this.mute.bind(this);
		this.unmute = this.unmute.bind(this);
		this.markNotificationAs = this.markNotificationAs.bind(this);
		this.deleteNotifications = this.deleteNotifications.bind(this);
		this.applyConfigChange = this.applyConfigChange.bind(this);
		this.getSources = this.getSources.bind(this);
		this.getMuteFilters = this.getMuteFilters.bind(this);
		this.getNotificationsPreferences = this.getNotificationsPreferences.bind(this);
	}

	/**
	 * Fetches all current mute filters.
	 */
	getMuteFilters() {
		return this.muteFilters;
	}

	/**
	 * Fetches all current notification related preferences
	 */
	async getNotificationsPreferences(): Promise<object> {
		// currrent preferences
		const disableDotOnToolbarIconKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON}`;
		const informationalNotificationToastPeriodKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS}`;
		const defaultSnoozePeriodKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS}`;
		const newNotificationHaloPeriodKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS}`;
		const showDotOnToolbarIconMutedKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS}`;
		const toastTimeoutOnActionalbeNotificationsKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS}`;

		async function applyDefaultPreferences() {
			await ConfigClient.setValues([
				{
					field: disableDotOnToolbarIconKey,
					value: NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON],
				},
				{
					field: informationalNotificationToastPeriodKey,
					value:
						NOTIFICATIONS_PREFERENCES_DEFAULTS[
							NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS
						],
				},
				{
					field: defaultSnoozePeriodKey,
					value: NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS],
				},
				{
					field: newNotificationHaloPeriodKey,
					value:
						NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS],
				},
				{
					field: showDotOnToolbarIconMutedKey,
					value:
						NOTIFICATIONS_PREFERENCES_DEFAULTS[
							NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS
						],
				},
				{
					field: toastTimeoutOnActionalbeNotificationsKey,
					value:
						NOTIFICATIONS_PREFERENCES_DEFAULTS[
							NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS
						],
				},
			]);

			return {
				[NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON],
				[NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[
						NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS
					],
				[NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS],
				[NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS],
				[NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[
						NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS
					],
				[NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS]:
					NOTIFICATIONS_PREFERENCES_DEFAULTS[NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS],
			};
		}

		// deprecated
		const __disableDotOnToolbarIconKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.DISABLE_DOT_ON_TOOLBAR_ICON}`;
		const __informationalNotificationToastPeriodKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.INFORMATIONAL_NOTIFICATION_TOAST_PERIOD_SECONDS}`;
		const __defaultSnoozePeriodKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.DEFAULT_SNOOZE_PERIOD_SECONDS}`;
		const __newNotificationHaloPeriodKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.NEW_NOTIFICATION_HALO_PERIOD_SECONDS}`;
		const __showDotOnToolbarIconMutedKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.SHOW_DOT_ON_TOOLBAR_ICON_MUTED_NOTIFICATIONS}`;
		const __toastTimeoutOnActionalbeNotificationsKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${NOTIFICATIONS_PREFERENCES_KEYS.TOAST_TIMEOUT_ON_ACTIONABLE_NOTIFICATIONS}`;

		const { err, data: notificationsPreferences } = await ConfigClient.getValues([
			// currrent preferences
			disableDotOnToolbarIconKey,
			informationalNotificationToastPeriodKey,
			defaultSnoozePeriodKey,
			newNotificationHaloPeriodKey,
			showDotOnToolbarIconMutedKey,
			toastTimeoutOnActionalbeNotificationsKey,

			// deprecated
			__disableDotOnToolbarIconKey,
			__informationalNotificationToastPeriodKey,
			__defaultSnoozePeriodKey,
			__newNotificationHaloPeriodKey,
			__showDotOnToolbarIconMutedKey,
			__toastTimeoutOnActionalbeNotificationsKey,
		]);

		if (!notificationsPreferences) {
			Logger.system.warn(
				"getNotificationsPreferences - Unable to get notificiations preferences - applying default values",
				err
			);
			return await applyDefaultPreferences();
		}

		let result = {};
		Object.values(NOTIFICATIONS_PREFERENCES_KEYS).forEach(async (key) => {
			const fullKey = `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${key}`;
			// If we do not have a preference value, then set a value from the deprecated config OR fallback to use the default values
			if (notificationsPreferences[fullKey] === null || typeof notificationsPreferences[fullKey] === "undefined") {
				const deprecatedPreferenceKey = `${DEPRECATED_FINSEMBLE_PREFERENCES_PATH}.${key}`;
				if (
					notificationsPreferences[deprecatedPreferenceKey] !== null &&
					typeof notificationsPreferences[deprecatedPreferenceKey] === "undefined"
				) {
					notificationsPreferences[fullKey] = notificationsPreferences[deprecatedPreferenceKey];
				} else {
					notificationsPreferences[fullKey] = NOTIFICATIONS_PREFERENCES_DEFAULTS[key];
				}

				this.setPreference(key, notificationsPreferences[fullKey]);
			}
			result[key] = notificationsPreferences[fullKey];
		});
		return result;
	}

	/**
	 * Fetches all unique existing sources.
	 */
	getSources() {
		let sourcesArray = Array.from(this.storageAbstraction.lastIssued.keys());
		const noSourceIndex = sourcesArray.indexOf(NO_SOURCE);
		if (noSourceIndex >= 0) {
			sourcesArray.splice(noSourceIndex, 1);
		}
		return sourcesArray;
	}

	/**
	 * Fetches all previously stored state from storage
	 */
	async fetchStateFromStorage() {
		this.storageAbstraction.notifications = asMap(await StorageHelper.fetchNotifications()) as Map<
			string,
			INotification
		>;
		this.storageAbstraction.lastIssued = asMap(await StorageHelper.fetchLastIssued()) as Map<string, ILastIssued>;
		this.storageAbstraction.snoozeTimers = asMap(await StorageHelper.fetchSnoozeTimers()) as Map<string, ISnoozeTimer>;
	}

	/**
	 * Helper method utilizing RouterClient's query method
	 *
	 * @param {string} channel
	 * @param {string} channelPrefix Will use the default prefix if null is passed
	 * @param data
	 * @param {Function} callback
	 */
	query(channel: string, data: {}, channelPrefix?: string | null, callback?: StandardCallback): Promise<any> {
		return new Promise<{}>(async (resolve, reject) => {
			try {
				if (!channelPrefix) {
					channelPrefix = ROUTER_ENDPOINTS.CHANNEL_PREFIX;
				}

				this.Logger.system.info(`sending message on ${channelPrefix + channel} channel`, data);

				const response = await this.RouterClient.query(
					channelPrefix + channel,
					data,
					() => {} // Ignoring callback. Use the promise to get the result
				);

				if (response.err) {
					this.Logger.system.error(`Error: `, response.err);
					reject(response.err);
				} else {
					this.Logger.system.log(`${channel} raw response: `, response);
					if (callback) {
						callback(null, response?.response?.data.data);
					}
					resolve(response?.response?.data.data);
				}
			} catch (e: any) {
				this.Logger.system.error(`Error: `, e);
				if (callback) {
					callback(e);
				}
				reject(e);
			}
		});
	}

	/**
	 * When incoming notifications arrive, lookup matching subscriptions and call necessary
	 * callbacks on subscription.
	 *
	 * @param {INotification} notification of INotification objects to broadcast.
	 * @private
	 */
	broadcastNotification(notification: INotification): void {
		this.Logger.system.log("Broadcasting Notification: ", notification.id);
		this.subscriptions.forEach((subscription) => {
			// Check if this notification matches any filters
			// For each notification that matches, expect a response and send it out.
			if (subscription.channel && ServiceHelper.filterMatches(subscription.filter, notification)) {
				this.query(subscription.channel, notification, null, () => {
					// maybe setReceivedReceipt
				}).then();
			}
		});
		if (
			this.config["service"]["proxyToWebApiFilter"] &&
			ServiceHelper.filterMatches(this.config["service"]["proxyToWebApiFilter"], notification)
		) {
			this.webApiNotify(notification);
		}
	}

	/**
	 * Sends a notification use the Web API
	 *
	 * @param notification
	 */
	webApiNotify(notification: INotification): void {
		const title: any[] = [];
		notification.title ? title.push(notification.title) : null;
		notification.title ? title.push(notification.headerText) : null;

		function convertNotificationToWebApi(notificationToConvert: INotification) {
			return {
				body: notificationToConvert.details,
				icon: notificationToConvert.contentLogo ? notificationToConvert.contentLogo : notificationToConvert.headerLogo,
			};
		}

		const options = convertNotificationToWebApi(notification);
		// WebApi Actions are only possible by implementing ServiceWorkerRegistration.showNotification()
		new Notification(title.join(" - "), options);
	}

	/**
	 * Remove the notification from the storage map if it exists
	 *
	 * @param {string} notificationId The id of the notification to remove from the storage map
	 */
	deleteFromStorage(notificationId: string) {
		const id = notificationId;
		if (this.storageAbstraction.snoozeTimers.has(id)) {
			this.storageAbstraction.snoozeTimers.delete(id);
		}

		if (this.storageAbstraction.notifications.has(id)) {
			this.storageAbstraction.notifications.delete(id);
		}
	}

	/**
	 * Sets a notifications 'isDeleted' flag to indicate its been removed
	 *
	 * @param {INotification[]} notifications The notifications to delete
	 *
	 */
	deleteNotifications(notifications: INotification[]): void {
		notifications.forEach((notification) => {
			if (notification.id) {
				this.deleteFromStorage(notification.id);
				notification.isDeleted = true;
				this.notify([notification]);
			}
		});
	}

	/**
	 * Takes removes a notification from a map and adds it on to the front to take advantage of
	 * maps being aware of the insertion order. (Makes it easier to know what is the oldest notification)
	 *
	 * @param notification
	 */
	addNotification(notification: INotification): void {
		if (notification.id) {
			if (this.storageAbstraction.notifications.has(notification.id)) {
				this.storageAbstraction.notifications.delete(notification.id);
			}
			this.storageAbstraction.notifications.set(notification.id, notification);
		}
	}

	/**
	 * Handles all messages on the 'action' endpoint/channel and sends them
	 * out to the service that knows how to deal with it.
	 *
	 * @param message
	 */
	performAction(message: any): Record<string, any> {
		this.Logger.system.info("Request to perform Actions", message);
		const { notifications, action } = message;
		const response = {
			message: "success",
			errors: new Array<{}>(),
		};

		notifications.forEach((notification: INotification) => {
			try {
				this.delegateAction(notification, action);
			} catch (error: any) {
				response.message = "fail";
				response.errors.push(error.message);
			}
		});
		return response;
	}

	/**
	 * Creates or updates notifications in Finsemble.
	 *
	 * @param {INotification[]} notifications from external source to be created or updated in Finsemble.
	 */
	notify(notifications: INotification[]): void {
		notifications.forEach(async (notification) => {
			// If we are not in devMode and this notification is devModeOnly, we discard it
			if (!this.devMode && notification.devModeOnly) return;
			let processedNotification = this.receiveNotification(notification);
			this.saveLastIssuedAt(processedNotification.source, processedNotification.issuedAt);
			processedNotification = ServiceHelper.setNotificationHistory(
				processedNotification,
				this.storageAbstraction.notifications,
				notification
			);

			const notificationsToPurge = await this.storeNotifications(processedNotification);

			// Deleted notification is added to purgeList don't broadcast here
			if (!processedNotification.isDeleted) {
				this.broadcastNotification(processedNotification);
			}

			notificationsToPurge.forEach((deleted) => {
				// Delete from the persisted storage and broadcast to the UI that this should be deleted
				StorageHelper.deleteValue(STORAGE_KEY_NOTIFICATION_PREFIX + deleted.id);
				this.broadcastNotification(deleted);
			});

			this.RouterClient.transmit("FinsembleNotificationsPreferencesChannel", "notificationReceived");
		});
	}

	/**
	 * Picks up any messages on the 'subscribe' endpoint/channel
	 *
	 * @param {ISubscription} subscription
	 * @return {string} a router channel on which notifications for this subscription will be sent.
	 */
	subscribe(subscription: ISubscription): {} {
		const channel = this.getChannel();
		subscription.id = this.getUuid();
		this.Logger.system.info("Successfully processed subscription: ", subscription);
		subscription.channel = channel;

		this.addToSubscription(subscription);
		return {
			id: subscription.id,
			channel: channel,
		};
	}

	/**
	 * Stores the time when a notification arrived from a specific source in finsemble.
	 *
	 * @param {string} source a notification that was updated. This notification can then be matched on using a filter to find out when different notifications were last updated.
	 * @param {string} issuedAt ISO8601 format string. When a notification was last delivered to Finsemble.
	 */
	saveLastIssuedAt(source: string | null | undefined, issuedAt: string): void {
		if (!source) {
			source = NO_SOURCE;
		}

		if (this.storageAbstraction.lastIssued.has(source)) {
			const d1 = new Date(issuedAt);
			const lastIssued = this.storageAbstraction.lastIssued.get(source);
			const d2 = lastIssued ? new Date(lastIssued.issuedAt) : d1;
			// Update only if the new date is newer
			if (d1 > d2) {
				this.storageAbstraction.lastIssued.set(source, new LastIssued(source, issuedAt));
				try {
					StorageHelper.storeLastIssued(this.storageAbstraction.lastIssued).then();
				} catch (e) {
					this.Logger.system.error("Failed to save lastIssued data", e);
				}
			}
		} else {
			this.storageAbstraction.lastIssued.set(source, new LastIssued(source, issuedAt));
			try {
				StorageHelper.storeLastIssued(this.storageAbstraction.lastIssued).then();
			} catch (e) {
				this.Logger.system.error("Failed to save lastIssued data", e);
			}
		}
	}

	/**
	 * Get a channel/endpoint the client will need to listen to
	 *
	 * @return string
	 */
	getChannel(): string {
		return `${ROUTER_ENDPOINTS.SUBSCRIBE}.${this.getUuid()}`;
	}

	/**
	 * Snoozes a notification
	 *
	 * @param notification
	 * @param action
	 * @param timeoutOverride
	 */
	snooze(notification: INotification, action: IAction, timeoutOverride?: number): INotification {
		this.removeFromSnoozeQueue(notification);
		let defaultTimeout = this.config.service.defaultSnoozePeriodSeconds;
		if (!defaultTimeout || defaultTimeout < 0) {
			this.Logger.system.warn(`Invalid snooze timeout in configuration - defaulting to 180 seconds`);
			defaultTimeout = 180;
		} else {
			defaultTimeout = defaultTimeout;
		}

		// Timeout is passed in seconds - we need to convert it to ms
		const timeout = timeoutOverride ?? action.milliseconds ?? defaultTimeout * 1000;

		const snoozeTimer = new SnoozeTimer();
		snoozeTimer.notificationId = notification.id;
		snoozeTimer.snoozeInterval = timeout;
		snoozeTimer.timeoutId = setTimeout(() => {
			if (notification.id) this.wake(notification.id);
		}, timeout);

		this.Logger.system.log(`Timing Snoozing Date ${Date.now()}`);
		this.Logger.system.log(`Timing Snoozing Seconds ${timeout}`);
		snoozeTimer.wakeEpochMilliseconds = Date.now() + timeout;
		this.Logger.system.log(`Timing Snoozing for ${snoozeTimer.wakeEpochMilliseconds}`);

		if (notification.id) this.storageAbstraction.snoozeTimers.set(notification.id, snoozeTimer);
		try {
			StorageHelper.storeSnoozeTimers(this.storageAbstraction.snoozeTimers).then();
		} catch (e) {
			this.Logger.system.error("Failed to store snooze timers", e);
		}
		notification.isSnoozed = true;
		return notification;
	}

	/**
	 * Wakes a snoozed notification
	 *
	 * @param notification
	 */
	private wake(notificationId: string) {
		let notificationToWake = this.storageAbstraction.notifications.get(notificationId);
		if (notificationToWake) {
			const action = new Action();
			action.id = this.getUuid();
			action.type = "FINSEMBLE:WAKE";
			notificationToWake = ServiceHelper.addPerformedAction(notificationToWake, action) as INotification;
			notificationToWake.isSnoozed = false;

			// if the notification marked as read and snoozed - when it wakes up we marked it as unread
			if (notificationToWake.isRead) {
				notificationToWake.isRead = false;
			}

			this.removeFromSnoozeQueue(notificationToWake);
			this.notify([notificationToWake]);
		}
	}

	/**
	 * Process a dismiss action request
	 *
	 * @param notification
	 */
	dismiss(notification: INotification): INotification {
		notification.isRead = true;
		return notification;
	}

	/**
	 * Process a spawn action request
	 *
	 * @param notification
	 * @param action
	 */
	spawn(notification: INotification, action: IAction): INotification {
		if (action.component) LauncherClient.spawn(action.component, action.spawnParams).then();
		return notification;
	}

	/**
	 * Validation for router based actions
	 *
	 * @param action
	 */
	validateForwardParams(action: IAction) {
		if (!action.channel) {
			throw new Error(`No channel set when trying to perform '${action.type}'`);
		}
	}

	/**
	 * Processes an unsubscribe request from the client
	 *
	 * @param subscriptionId
	 */
	public unsubscribe(subscriptionId: string) {
		this.Logger.system.log(`Removing notification subscription: ${subscriptionId}`);
		if (this.subscriptions.has(subscriptionId)) {
			this.subscriptions.delete(subscriptionId);
		}
	}

	/**
	 * Config change callback
	 *
	 * @param err
	 * @param config
	 */
	applyConfigChange: StandardCallback = (err, config) => {
		if (err) {
			this.Logger.system.error(`Unable to get config err: ${err}`);
		}
		this.config = ServiceHelper.normaliseConfig(config);
	};

	/**
	 * Mute filter change callback
	 *
	 * @param err
	 * @param config
	 */
	applyMuteFilterChange: StandardCallback = (err, config) => {
		if (err) {
			this.Logger.system.error(`Unable to get config err: ${err}`);
		}

		if (config) {
			// configClient.getValue() and configClient.addListener return different formats
			if (config.value) {
				config = config.value;
			}
			this.muteFilters = config;
		}
	};

	/**
	 * Delegate the action to any service that is registered on the correct channel
	 *
	 * @see notificationsBuiltInActionsService for an example
	 *
	 * @param notification
	 * @param action
	 *
	 */
	private delegateAction(notification: INotification, action: IAction): void {
		/**
		 * Action is considered completed by the time it hits the service
		 * ie. (the request for action has been received)
		 * Discussion here https://chartiq.slack.com/archives/CPYQ16K7H/p1574357206003200
		 */
		notification = ServiceHelper.addPerformedAction(notification, action) as INotification;

		/**
		 * If an action is performed on a notification, it should not be snoozed anymore.
		 */
		this.removeFromSnoozeQueue(notification);

		this.Logger.system.info(`Action type: ${action.type}`);
		// Pick up any updated states from performing the action
		switch (action.type.toUpperCase()) {
			case ActionTypes.SNOOZE:
				notification = this.snooze(notification, action);
				this.notify([notification]);
				break;
			case ActionTypes.SPAWN:
				notification = this.spawn(notification, action);
				break;
			case ActionTypes.QUERY:
				notification = this.forwardAsQuery(notification, action);
				break;
			case ActionTypes.TRANSMIT:
				notification = this.forwardAsTransmit(notification, action);
				break;
			case ActionTypes.PUBLISH:
				notification = this.forwardAsPublish(notification, action);
				break;
			case ActionTypes.DISMISS:
				notification = this.dismiss(notification);
				this.notify([notification]);
				break;
			default:
				this.Logger.system.error(`Unable to perform action '${action.type}' on notification`);
				return;
		}
		this.Logger.system.info("Updated notification state", notification);

		// Mark the notification read. action.markAsRead not set defaults to true
		// SNOOZE + DISMISS actions are special cases
		if (
			!([ActionTypes.SNOOZE, ActionTypes.DISMISS] as string[]).includes(action.type.toUpperCase()) &&
			(typeof action.markAsRead === "undefined" || action.markAsRead === true)
		) {
			notification.isRead = true;
			// Broadcast the notification only if the state has changed - isRead is the only change in state when performing an action
			this.notify([notification]);
		}
	}

	/**
	 * Marks a notification as read or unread depending on the isRead status received (defaults to false).
	 *
	 * @param notifications {INotification[]}
	 */
	private markNotificationAs({
		notifications,
		isRead = false,
	}: {
		notifications: INotification[];
		isRead: boolean;
	}): INotification[] {
		this.Logger.system.info("Received state markAs", notifications);

		const newNotifications: INotification[] = notifications.map((notification) => {
			this.removeFromSnoozeQueue(notification);
			let clone = fastClone(notification) as INotification;

			clone.isRead = isRead;

			const action = new Action();
			action.id = this.getUuid();
			action.type = `FINSEMBLE:${isRead ? "READ" : "UNREAD"}`;
			clone = ServiceHelper.addPerformedAction(clone, action);
			if (isRead && notification.isSnoozed) {
				clone.isSnoozed = false;
			}
			return clone;
		});

		this.notify(newNotifications);
		return newNotifications;
	}

	/**
	 * Initial processing done on a notification when it comes into the system
	 *
	 * @param notification
	 */
	private receiveNotification(notification: INotification): INotification {
		this.Logger.system.info("Received state", notification);

		// If we receive a notification object that contais an options key
		// we will assume it is a browser notification as per the official API
		// https://developer.mozilla.org/en-US/docs/Web/API/Notification/Notification
		// and we will make a best-effort attempt to map some of the fields
		if (Object.keys(notification).includes("options") && notification["options"]) {
			const { options } = notification as any;
			notification.details = options.body;
			notification.headerLogo = options.icon;

			if (options.format && (options.format as string).includes("com.symphony")) {
				notification.source = "Symphony";
				notification.title = `Message from ${notification.title}`;
			} else {
				notification.source = options.finsembleSource || "Unknown Source";
			}
		}
		notification = ServiceHelper.applyDefaults(this.config, notification);

		let clone = fastClone(notification);

		if (!clone.id) {
			clone.id = this.getUuid();
		}

		if (!clone.issuedAt) {
			clone.issuedAt = new Date().toISOString();
		}

		if (!clone.receivedAt) {
			const action = new Action();
			action.id = this.getUuid();
			action.type = "FINSEMBLE:RECEIVED";
			if (ServiceHelper.matchesMuteFilters(notification, this.muteFilters)) {
				clone.isMuted = true;
				this.Logger.system
					.log(`Mute notificationID:${notification.id}, '${notification.title}', source:'${notification.source}', type: 
				'${notification.type}'`);
			}
			clone = ServiceHelper.addPerformedAction(clone, action);
			clone.receivedAt = new Date().toISOString();
		}
		this.Logger.system.info("Applied state", clone);
		return clone;
	}

	/**
	 * Stores the notifications in the local caches, collects expired notifications and persists to storage
	 *
	 * @param notification {INotification}
	 */
	private async storeNotifications(notification: INotification): Promise<INotification[]> {
		let notificationsToDelete: INotification[] = [];
		// Don't add the notification to storage if it's marked as deleted
		if (!notification.isDeleted) {
			// Remove a notification from the map and add it to the end
			this.addNotification(notification);
		}

		notificationsToDelete = ServiceHelper.getItemsToPurge(this.storageAbstraction.notifications, {
			maxNotificationRetentionPeriodSeconds: this.config.service.maxNotificationRetentionPeriodSeconds,
			maxNotificationsToRetain: this.config.service.maxNotificationsToRetain,
		});
		notificationsToDelete.forEach((toBeDeleted) => {
			if (toBeDeleted.id) {
				this.deleteFromStorage(toBeDeleted.id);
				toBeDeleted.isDeleted = true;
			}
		});

		if (notification.isDeleted) {
			notificationsToDelete.push(notification);
		}

		await StorageHelper.persistNotification(this.storageAbstraction.notifications, notification);
		await StorageHelper.storeSnoozeTimers(this.storageAbstraction.snoozeTimers);

		return notificationsToDelete;
	}

	/**
	 * Helper method utilizing RouterClient's addResponder method
	 *
	 * @param {string} channel
	 * @param {Function} dataProcessor
	 * @param {string|null} channelPrefix
	 */
	addResponder(channel: string, dataProcessor: (notification: any) => any, channelPrefix?: string) {
		if (channelPrefix == null) {
			channelPrefix = ROUTER_ENDPOINTS.CHANNEL_PREFIX;
		}

		this.Logger.system.info(`Adding responder for endpoint: ${channelPrefix + channel}`);

		this.RouterClient.addResponder(channelPrefix + channel, async (err, message) => {
			this.Logger.system.info(`Message received on ${channel}: `, message);
			if (err) {
				this.Logger.system.error(`Failed to setup ${channel} responder`, err);
				return;
			}

			try {
				this.Logger.system.info(`Processing message on channel ${channel}: `, message);
				const returnVal = await dataProcessor(message?.data);
				this.Logger.system.info(`Message response on ${channel}: `, returnVal);
				message?.sendQueryResponse(null, {
					status: "success",
					data: returnVal,
				});
			} catch (err2: any) {
				this.Logger.system.error(`Failed to process query`, err2);
				message?.sendQueryResponse(err2);
			}
		});
	}

	setPreferenceChangeListener() {
		this.RouterClient.addListener("FinsembleUserPreferencesChannel", async (error: any, response: any) => {
			const preferencesToStore = response.data.notificationPreferences;
			if (preferencesToStore) {
				Object.keys(preferencesToStore).forEach((preferenceKey) => {
					this.setPreference(preferenceKey, preferencesToStore[preferenceKey]);
				});
			}
		});
	}

	setPreference(preference: string, preferenceValue: any) {
		this.Logger.system.info("Updating notifications user preferences", preference);
		ConfigClient.setPreference(
			{ field: `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.${preference}`, value: preferenceValue },
			(err: any) => {
				if (err) {
					this.Logger.system.error("Could not save mute preference", preference);
				}
			}
		);
	}

	/**
	 * Setup callback on notify channel
	 */
	private setupNotify(): void {
		this.addResponder(ROUTER_ENDPOINTS.NOTIFY, this.notify);
	}

	/**
	 * Setup callback on notify channel
	 */
	private setupLastIssued(): void {
		this.addResponder(ROUTER_ENDPOINTS.LAST_ISSUED, this.getLastIssued);
	}

	/**
	 * Setup callback on notify channel
	 */
	private setupFetchHistory(): void {
		this.addResponder(ROUTER_ENDPOINTS.FETCH_HISTORY, this.fetchHistory);
	}

	/**
	 * Setup callback on notify channel
	 */
	private setupMute(): void {
		this.addResponder(ROUTER_ENDPOINTS.MUTE, this.mute);
		this.addResponder(ROUTER_ENDPOINTS.UNMUTE, this.unmute);
	}

	/**
	 * Setup callback on subscribe channel
	 */
	private setupSubscribe() {
		this.addResponder(ROUTER_ENDPOINTS.SUBSCRIBE, this.subscribe);
	}

	/**
	 * Setup callback on unsubscribe channel
	 */
	private setupUnsubscribe() {
		this.addResponder(ROUTER_ENDPOINTS.UNSUBSCRIBE, this.unsubscribe);
	}

	/**
	 * Setup callback on mark unread channel
	 */
	private setupMarkUnread(): void {
		this.addResponder(ROUTER_ENDPOINTS.MARK_UNREAD, this.markNotificationAs);
	}

	/**
	 * Setup callback on mark read channel
	 */
	private setupMarkRead(): void {
		this.addResponder(ROUTER_ENDPOINTS.MARK_READ, this.markNotificationAs);
	}

	/**
	 * Setup callback on delete notification
	 */
	private setupDelete(): void {
		this.addResponder(ROUTER_ENDPOINTS.DELETE, this.deleteNotifications);
	}

	/**
	 * Setup callback on action channel
	 */
	private setupAction() {
		this.addResponder(ROUTER_ENDPOINTS.PERFORM_ACTION, this.performAction);
	}

	/**
	 * Setup the callback on get sources.
	 */
	private setupSources() {
		this.addResponder(ROUTER_ENDPOINTS.SOURCES, this.getSources);
	}

	/**
	 * Setup the callback on get mute filters.
	 */
	private setupMuteFilters() {
		this.addResponder(ROUTER_ENDPOINTS.MUTE_FILTERS, this.getMuteFilters);
	}

	/**
	 * Setup the callback on get notification prefences.
	 */
	private setupPreferences() {
		this.addResponder(ROUTER_ENDPOINTS.PREFERENCES, this.getNotificationsPreferences);
	}

	/**
	 * Setup UI PubSub Channel
	 */
	private async setupUIPubSub() {
		const data = await StorageClient.get({ topic: "finsemble.notifications", key: "last.position" });
		const position = data?.position ? data.position : null;
		let pubSubState: NotificationServiceState = {
			showDrawer: false,
			showCenter: false,
			poppedOut: data?.poppedOut ? data.poppedOut : false,
			position: position,
			toasterMonitor: "0",
			overFlowMenu: {
				notification: false,
				overflowCount: false,
				clickCoordinates: false,
			},
		};

		/**
		 * This is a pubsub "hook" that runs whenever a client publishes a notification. Its primary purpose
		 * is to keep the service's local state (pubSubState) up to date with the latest publications from clients.
		 *
		 * This hook also ensures that the NotificationsCenter is open before continuing on to publish the notification.
		 */
		const spawnIfClosed: StandardCallback = async (error, request): Promise<void> => {
			if (error) return;

			const newUIState: NotificationServiceState = request.data;

			const activeComponents = (await launcherClient.getActiveDescriptors())?.data;
			const componentType = this.config.service.notificationsCenterComponentType || "NotificationsCenter";
			const notificationCenterIsRunning =
				activeComponents && Object.keys(activeComponents).some((key) => key === componentType);

			if (!notificationCenterIsRunning && newUIState?.showCenter !== pubSubState.showCenter) {
				await LauncherClient.showWindow(
					{
						windowName: "notifications-center",
						componentType,
					},
					{
						spawnIfNotFound: true,
						/**
						 * In order to ensure that the NotificationCenter is spawned on the same window as the client
						 * that published the notification, we must pass spawn() the windowName of the originating notification,
						 * otherwise it would be spawned on the same window as this invisible notification service.
						 */
						relativeWindow: {
							windowName: newUIState.mostRecentWindowName ?? Startup.getInstance().currentStartupData.windowName,
						},
					}
				);
			}
			pubSubState = newUIState;
			request.sendNotifyToAllSubscribers(null, newUIState);
		};
		this.RouterClient.addPubSubResponder(NOTIFICATIONS_PUBSUB_TOPIC, pubSubState, {
			publishCallback: spawnIfClosed,
		});
	}

	/**
	 * Store the subscription so it can be referenced and also unsubscribed from later.
	 *
	 * @param subscription
	 */
	private addToSubscription(subscription: ISubscription) {
		if (subscription.id) this.subscriptions.set(subscription.id, subscription);
	}

	/**
	 * Process the request for the query action type
	 * @param notification
	 * @param action
	 */
	private forwardAsQuery(notification: INotification, action: IAction): INotification {
		this.validateForwardParams(action);
		if (action.channel) {
			try {
				this.query(
					action.channel,
					{
						notification: notification,
						actionPayload: action.payload,
					},
					""
				).then();
			} catch (error) {
				this.Logger.system.error(`Error performing action on channel channel: '${action.channel}'`);
				notification.isRead = false;
			}
		}

		return notification;
	}

	/**
	 * Helper method utilizing RouterClient's transmit method
	 *
	 * @param channel
	 * @param channelPrefix
	 * @param payload
	 */
	transmit(channel: string, payload: any, channelPrefix?: string) {
		if (channelPrefix == null) {
			channelPrefix = ROUTER_ENDPOINTS.CHANNEL_PREFIX;
		}
		this.Logger.info(`Transmitting on channel: ${channelPrefix + channel}`);
		this.RouterClient.transmit(channelPrefix + channel, payload);
	}

	/**
	 * Process the request for the transmit action type
	 *
	 * @param notification
	 * @param action
	 */
	private forwardAsTransmit(notification: INotification, action: IAction): INotification {
		this.validateForwardParams(action);
		if (action.channel) {
			this.transmit(
				action.channel,
				{
					notification: notification,
					actionPayload: action.payload,
				},
				""
			);
		}

		return notification;
	}

	/**
	 * Helper method utilizing RouterClient's publish method
	 *
	 * @param channel
	 * @param channelPrefix
	 * @param payload
	 */
	publish(channel: string, payload: any, channelPrefix?: string) {
		if (channelPrefix == null) {
			channelPrefix = ROUTER_ENDPOINTS.CHANNEL_PREFIX;
		}

		this.Logger.info(channelPrefix);
		this.Logger.info(`Publishing on channel: ${channelPrefix + channel}`);
		this.RouterClient.publish(channelPrefix + channel, payload);
	}

	/**
	 * Process the request for the publish action type
	 *
	 * @param notification
	 * @param action
	 */
	private forwardAsPublish(notification: INotification, action: IAction): INotification {
		this.validateForwardParams(action);
		if (action.channel) {
			this.publish(
				action.channel,
				{
					notification: notification,
					actionPayload: action.payload,
				},
				""
			);
		}

		return notification;
	}

	/**
	 * Gets the last issued date for the source provided
	 *
	 * If no source is provided it will get the latest issue date for all notifications
	 * (I.e the last time any notification was issue to the service)
	 *
	 * @param source
	 */
	private getLastIssued(source?: string): string | undefined {
		this.Logger.system.info(`Finding last issued for source: '${source}'`);
		let returnValue: string | undefined = "";
		if (source && this.storageAbstraction.lastIssued.has(source)) {
			returnValue = this.storageAbstraction.lastIssued.get(source)?.issuedAt;
		} else {
			this.storageAbstraction.lastIssued.forEach((lastIssued: ILastIssued) => {
				if (!returnValue) {
					returnValue = lastIssued.issuedAt;
				} else {
					const d1 = new Date(returnValue);
					const d2 = new Date(lastIssued.issuedAt);
					if (d2 > d1) {
						returnValue = lastIssued.issuedAt;
					}
				}
			});
		}

		return returnValue;
	}

	private getAPageOfNotifications({ filter, pageSize, sentAfter, sentBefore, pageNumber }): INotification[] {
		let count = 0;
		let items = 0;
		// Pagination/scrolling Requires notifications to be returned most recent first
		const reversedNotifications = Array.from(this.storageAbstraction.notifications).reverse();
		return reversedNotifications.flatMap(([key, notification]) => {
			// We've already filled our page size - skip the rest
			if (pageSize && items >= pageSize) {
				return [];
			}

			const notificationDate = notification.receivedAt ? new Date(notification.receivedAt) : new Date();
			if (sentAfter) {
				this.Logger.system.info("Notification date", notification.receivedAt);
				// We only want notifications sent after - skip if earlier
				if (notificationDate <= sentAfter) {
					return [];
				}
			}

			// We only want notifications sent before - skip if later
			if (sentBefore && notificationDate >= sentBefore) {
				return [];
			}

			if (filter && !ServiceHelper.filterMatches(filter, notification)) {
				// If there is a filter and the filter does not match the notification - skip it
				return [];
			}

			if (pageNumber && count < (pageNumber - 1) * pageSize) {
				count++;
				// Pagination
				return [];
			}

			// Notification date is greater than date param or not date param is set
			// Filter matches or not filter is set
			const storedNotification = this.storageAbstraction.notifications.get(key);
			if (storedNotification) {
				items++;
				return [storedNotification];
			}
			return [];
		});
	}

	/**
	 * Executes the fetch instruction to return notifications stored by the service
	 *
	 * If source is not provided it will get the latest issue from the all registered sources
	 *
	 * @param message
	 */
	private fetchHistory(message: any): INotification[] {
		this.Logger.system.info("Fetch history request with params", message);

		let sentAfter;
		let sentBefore;
		let pageSize;
		let pageNumber;
		let { options } = message;

		if (options && typeof options === "string") {
			this.Logger.system.deprecated(
				`FetchHistory: string parameter use has been deprecated.  Use object form {sentAfter: "${options}"} instead`
			);
			sentAfter = options;
			this.Logger.system.info("Since date", sentAfter);
		} else if (options && typeof options === "object") {
			({ sentAfter, sentBefore, pageSize, pageNumber } = options);
			if (pageSize && !pageNumber) {
				pageNumber = 1;
			}
		}

		if (sentAfter) {
			sentAfter = sentAfter == "startup" ? this.serviceStartTime : new Date(sentAfter);
		}

		if (sentBefore) {
			sentBefore = sentBefore == "startup" ? this.serviceStartTime : new Date(sentBefore);
		}

		return this.getAPageOfNotifications({ filter: message.filter, sentAfter, sentBefore, pageNumber, pageSize });
	}

	/**
	 * Mutes notifications of a given type.
	 *
	 * @param message
	 */
	private unmute(message: any): void {
		this.Logger.system.info("Unmute request with params", message);

		const { filter } = message;

		const newMuteFilters = this.muteFilters.slice();

		if (Array.isArray(filter)) {
			filter.forEach((element) => {
				this.removeFromMuteFilters(newMuteFilters, element);
			});
		} else {
			this.removeFromMuteFilters(newMuteFilters, filter);
		}

		ConfigClient.setPreference(
			{ field: `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.mute`, value: newMuteFilters },
			(err) => {
				if (err) {
					this.Logger.system.error("Could not save unmute preference", message);
				}
			}
		);
	}

	/**
	 * Unmutes notifications of a given type.
	 *
	 * @param message
	 */
	private mute(message: any): void {
		this.Logger.system.info("Mute request with params", message);
		const { filter } = message;

		const newMuteFilters = this.muteFilters.slice();

		if (Array.isArray(filter)) {
			filter.forEach((element) => {
				this.addToMuteFilters(newMuteFilters, element);
			});
		} else {
			this.addToMuteFilters(newMuteFilters, filter);
		}

		ConfigClient.setPreference(
			{ field: `${FINSEMBLE_NOTIFICATIONS_SERVICE_CONFIG_PATH}.mute`, value: newMuteFilters },
			(err) => {
				if (err) {
					this.Logger.system.error("Could not save mute preference", message);
				}
			}
		);
	}

	private addToMuteFilters(newMuteFilters, filter: IMuteFilter) {
		let isInArray = false;
		this.muteFilters.forEach((muteFilter: IMuteFilter) => {
			if (muteFilter.source === (filter as IMuteFilter).source && muteFilter.type === (filter as IMuteFilter).type) {
				isInArray = true;
			}
		});

		if (!isInArray) {
			newMuteFilters.push(filter);
		}
	}

	private removeFromMuteFilters(muteFilters, filter: IMuteFilter) {
		// Keep filters that don't match
		let condition = (muteFilter) => muteFilter.source !== filter.source || muteFilter.type !== filter.type;
		this.filterMutate(muteFilters, condition);
	}

	/**
	 * Filter implementation that WILL mutate the input array.
	 */
	private filterMutate(arr, cb) {
		for (let index = arr.length - 1; index >= 0; index = index - 1) {
			if (!cb(arr[index])) arr.splice(index, 1);
		}
	}

	/**
	 * Generates a UUID
	 */
	protected getUuid(): string {
		return uuidv4();
	}

	private removeFromSnoozeQueue(notification: INotification) {
		if (notification.id && this.storageAbstraction.snoozeTimers.has(notification.id)) {
			const storedNotification = this.storageAbstraction.snoozeTimers.get(notification.id);
			if (storedNotification) clearTimeout(storedNotification.timeoutId as any);
			this.storageAbstraction.snoozeTimers.delete(notification.id);
			try {
				StorageHelper.storeSnoozeTimers(this.storageAbstraction.snoozeTimers).then();
			} catch (e) {
				this.Logger.system.error("Failed to store snooze timers", e);
			}
		}
	}

	/**
	 * On FSBL Boot it checks if any notification's sleep timers have expired.
	 * Wakes and broadcasts them if they have or puts them back to sleep if they haven't
	 */
	private wakeSnoozeFromLoad() {
		const toSnooze: INotification[] = [];
		const toWake: INotification[] = [];

		this.storageAbstraction.snoozeTimers.forEach((timer, id) => {
			const notification = this.storageAbstraction.notifications.get(id);

			if (notification) {
				const timerEpoch = timer.wakeEpochMilliseconds;
				if (timerEpoch && Date.now() >= timerEpoch) {
					toWake.push(notification);
				} else {
					toSnooze.push(notification);
				}
			}
		});

		toSnooze.forEach((notification) => {
			if (notification.id) {
				const timer = this.storageAbstraction.snoozeTimers.get(notification.id);
				const wakeEpochMilliseconds = timer?.wakeEpochMilliseconds ?? 0;
				this.Logger.system.log(`Timing Timestamp ${wakeEpochMilliseconds}`);
				this.Logger.system.log(`Timing Now Timestamp ${Date.now()}`);
				this.Logger.system.log("Timing: " + `${wakeEpochMilliseconds - Date.now()}`);
				this.snooze(notification, new Action(), Math.max(SNOOZE_BOOT_WAIT, wakeEpochMilliseconds - Date.now()));
			}
		});

		toWake.forEach((notification) => {
			setTimeout(() => {
				if (notification.id) this.wake(notification.id);
			}, SNOOZE_BOOT_WAIT);
		});
	}
}

const serviceInstance = new NotificationService();
export default serviceInstance;
